// Story 022 + 029 — Split-view mount + filmstrip-redirect glue.
//
// Two responsibilities:
//
//  1. Renders `<SplitViewPanel/>` only while the store says `active`.
//  2. While split-view is active, intercepts filmstrip activations and
//     reroutes them to the secondary pane instead of swapping the
//     page-level active image. The filmstrip itself doesn't know
//     about split-view — its click handler still calls
//     `imageActivationStore.setActive(id)`. The interceptor listens to
//     that store, snapshots the click, routes it to the split-view
//     store, and restores the primary id so the host page (and any
//     other widget reading the active id) stays anchored.
//
// AC4 of story 022 specifies the exact behaviour: a filmstrip click
// while split-view is open populates the SECONDARY slot without
// disturbing the PRIMARY. This effect implements that without
// teaching the filmstrip about split-view (preserves single-source-of-
// truth for image activation in normal usage).
import { useEffect, useRef } from "react";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { type DefectEditPermission } from "../editPermission.js";
import { useSplitViewStore } from "./splitViewStore.js";
import { SplitViewPanel } from "./SplitViewPanel.js";

interface SplitViewOverlayProps {
  dataAcquisitionId: string;
  editPermission: DefectEditPermission;
}

export function SplitViewOverlay({
  dataAcquisitionId,
  editPermission,
}: SplitViewOverlayProps): React.ReactElement | null {
  const active = useSplitViewStore((s) => s.active);
  const primaryImageId = useSplitViewStore((s) => s.primaryImageId);
  const setSecondaryImage = useSplitViewStore((s) => s.setSecondaryImage);

  // Track the active-image-id we last saw so we can detect when it
  // changes WHILE split-view is open. We deliberately don't subscribe
  // via a selector — useEffect-style reactivity is cleaner here than
  // selector rerenders.
  const lastActiveRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active) {
      lastActiveRef.current = useImageActivationStore.getState().activeImageId;
      return;
    }
    // Snapshot the current active id at open time so we don't treat
    // it as a "new click".
    lastActiveRef.current = primaryImageId;

    const unsubscribe = useImageActivationStore.subscribe((state) => {
      const next = state.activeImageId;
      if (next === lastActiveRef.current) return;
      lastActiveRef.current = next;
      // The filmstrip just published `next`. Route it to the
      // secondary slot AND restore the primary as the host page's
      // active image — the underlying AnalysisFeature uses
      // activeImageId for the (currently hidden) Viewer + Tiles
      // widgets, and the Sketch widget's radius read.
      if (next && next !== primaryImageId) {
        setSecondaryImage(next);
        // Restore. Schedule via microtask so we don't fight the
        // store's own commit cycle.
        queueMicrotask(() => {
          useImageActivationStore
            .getState()
            .setActive(primaryImageId ?? null);
        });
      }
    });
    return () => {
      unsubscribe();
    };
  }, [active, primaryImageId, setSecondaryImage]);

  // Hide the page-level Filmstrip widget while split-view is open so
  // its `role="option"` thumbnails don't collide with the SplitView-
  // embedded filmstrip rail (story 022 AC4 — Playwright strict mode
  // would fail if two listboxes both had options with the same
  // accessible name).
  useEffect(() => {
    if (!active) return;
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-aero-split-view-hide-filmstrip", "true");
    styleEl.textContent = `
      [data-aero-split-view-region] ~ * [role="region"][aria-label="Filmstrip"],
      [role="region"][aria-label="Filmstrip"] {
        display: none !important;
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      try {
        document.head.removeChild(styleEl);
      } catch {
        // already gone
      }
    };
  }, [active]);

  if (!active) return null;
  return (
    <SplitViewPanel
      dataAcquisitionId={dataAcquisitionId}
      editPermission={editPermission}
    />
  );
}
